import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { isAdminApiEnabled } from "../../config/adminApiFeature";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  bulkPatchContactMessages,
  bulkRemoveContactMessages,
  fetchContactMessagesList,
} from "../../store/slices/contactMessagesSlice";
import {
  ADMIN_TASK_STATUS_OPTIONS,
  adminStatusBadgeClass,
  adminStatusHint,
  adminStatusLabel,
  canonicalAdminStatus,
} from "./adminStatusUi";
import {
  AdminInboxBulkBar,
  AdminInboxSelectAllHeader,
  useAdminInboxSelection,
} from "./AdminInboxBulkBar";
import { highlightSearchText } from "./adminSearchHighlight";

function buildContactListParams(debouncedQ, status, sort) {
  const sortParam = sort === "newest" ? "createdAt:desc" : "createdAt:asc";
  const params = { sort: sortParam };
  if (debouncedQ.trim()) params.q = debouncedQ.trim();
  if (status !== "all") params.status = status;
  return params;
}

const SKELETON_ROWS = 6;

export default function AdminContactPage() {
  const apiOn = isAdminApiEnabled();
  const dispatch = useAppDispatch();
  const apiItems = useAppSelector((s) => s.contactMessages.list.items);
  const listStatus = useAppSelector((s) => s.contactMessages.listStatus);
  const rows = useMemo(() => (apiOn ? apiItems : []), [apiOn, apiItems]);
  const [q, setQ] = useState("");
  const [newsletter, setNewsletter] = useState("all");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("newest");
  const debouncedQ = useDebouncedValue(q, 400);

  useEffect(() => {
    if (!apiOn) return;
    dispatch(fetchContactMessagesList(buildContactListParams(debouncedQ, status, sort)));
  }, [apiOn, dispatch, debouncedQ, status, sort]);

  const hasActiveFilters = Boolean(
    q.trim() || newsletter !== "all" || status !== "all"
  );

  const filtered = useMemo(() => {
    let list = [...rows];
    const qq = q.trim().toLowerCase();
    if (qq) {
      list = list.filter((r) => {
        const blob = `${r.fullName} ${r.email} ${r.company} ${r.message} ${r.country}`.toLowerCase();
        return blob.includes(qq);
      });
    }
    if (newsletter === "yes") list = list.filter((r) => r.newsletter);
    if (newsletter === "no") list = list.filter((r) => !r.newsletter);
    if (status !== "all") {
      list = list.filter((r) => canonicalAdminStatus(r.status) === status);
    }
    list.sort((a, b) => {
      const ta = new Date(a.createdAt).getTime();
      const tb = new Date(b.createdAt).getTime();
      return sort === "newest" ? tb - ta : ta - tb;
    });
    return list;
  }, [rows, q, newsletter, status, sort]);

  const renderSkeletonRows = useCallback(
    () =>
      Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <tr key={`sk-${i}`} className="admin-dash__table-skeleton-row" aria-hidden>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 22 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell admin-dash__table-skeleton-cell--tiny" />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell admin-dash__table-skeleton-cell--short" />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 100 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 160 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 72 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 200 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 100 }} />
          </td>
          <td>
            <div className="admin-dash__sk admin-dash__table-skeleton-cell" style={{ maxWidth: 48 }} />
          </td>
        </tr>
      )),
    []
  );

  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("new");
  const selection = useAdminInboxSelection(filtered);

  const tableBusy =
    apiOn && listStatus === "loading" && rows.length === 0;
  const showBulkChrome = apiOn && !tableBusy && filtered.length > 0;

  const clearSelection = selection.clear;
  const runBulk = useCallback(
    async (fn) => {
      setBulkBusy(true);
      try {
        await dispatch(fn).unwrap();
        clearSelection();
      } catch {
        /* toasts from thunk */
      } finally {
        setBulkBusy(false);
      }
    },
    [dispatch, clearSelection]
  );

  const onBulkMarkRead = useCallback(() => {
    const ids = selection.selectedIds;
    if (!ids.length) return;
    runBulk(
      bulkPatchContactMessages({
        ids,
        body: { seenAt: new Date().toISOString() },
      })
    );
  }, [runBulk, selection]);

  const onBulkApplyStatus = useCallback(() => {
    const ids = selection.selectedIds;
    if (!ids.length) return;
    runBulk(
      bulkPatchContactMessages({
        ids,
        body: { status: bulkStatus },
      })
    );
  }, [bulkStatus, runBulk, selection]);

  const onBulkDelete = useCallback(() => {
    const ids = selection.selectedIds;
    if (!ids.length) return;
    runBulk(bulkRemoveContactMessages(ids));
  }, [runBulk, selection]);

  const emptyMessage =
    !apiOn
      ? {
          title: "Admin API is off",
          sub: "Enable the admin API for this build and sign in to load messages from your backend.",
        }
      : rows.length === 0 && !hasActiveFilters
        ? {
            title: "No contact messages yet",
            sub: "New messages will appear here after they are stored by your backend.",
          }
        : {
            title: "No matching messages",
            sub: "Try clearing filters or broadening your search.",
          };

  return (
    <div className="admin-dash__page">
      <header className="admin-dash__page-head">
        <p className="admin-dash__page-eyebrow">Inbox</p>
        <h1 className="admin-dash__h1">Contact messages</h1>
        <p className="admin-dash__lede">
          Filter by the same client pipeline stages as requests, then search by name or message. The
          newsletter filter helps segment follow-ups.
        </p>
      </header>

      <div className="admin-dash__filters-top" role="search" aria-label="Filter messages">
        <div className="admin-dash__filter-group admin-dash__filter-group--grow">
          <span className="admin-dash__filter-group-label">Search</span>
          <input
            className="admin-dash__input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name, email, message…"
          />
        </div>
        <div className="admin-dash__filter-group">
          <span className="admin-dash__filter-group-label">Status</span>
          <select
            className="admin-dash__input"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="all">All</option>
            {ADMIN_TASK_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="admin-dash__filter-group">
          <span className="admin-dash__filter-group-label">Newsletter</span>
          <select
            className="admin-dash__input"
            value={newsletter}
            onChange={(e) => setNewsletter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="yes">Opted in</option>
            <option value="no">Not opted in</option>
          </select>
        </div>
        <div className="admin-dash__filter-group">
          <span className="admin-dash__filter-group-label">Sort</span>
          <select
            className="admin-dash__input"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>
        <p className="admin-dash__filters-meta">
          Showing {filtered.length} of {rows.length} messages
        </p>
      </div>

      {showBulkChrome ? (
        <AdminInboxBulkBar
          selectedCount={selection.selectedCount}
          onClear={selection.clear}
          onMarkRead={onBulkMarkRead}
          bulkStatus={bulkStatus}
          onBulkStatusChange={setBulkStatus}
          onApplyStatus={onBulkApplyStatus}
          onRequestDelete={onBulkDelete}
          busy={bulkBusy}
          noun="messages"
        />
      ) : null}

      <div className="admin-dash__table-panel">
        <div className="admin-dash__table-wrap admin-dash__table-wrap--scroll">
          <table className="admin-dash__table">
            <caption className="admin-dash__sr-only">
              Contact messages list. Preview shows excerpt; open a row for full details.
            </caption>
            <thead>
              <tr>
                {showBulkChrome ? (
                  <AdminInboxSelectAllHeader
                    allSelected={selection.allSelected}
                    someSelected={selection.someSelected}
                    onToggleAll={selection.toggleAllVisible}
                    disabled={bulkBusy}
                  />
                ) : (
                  <th scope="col" className="admin-dash__th-select" aria-hidden />
                )}
                <th scope="col" className="admin-dash__th-dot-col">
                  <span className="admin-dash__sr-only">Unread</span>
                </th>
                <th scope="col">
                  <span className="admin-dash__th-stack">
                    <span className="admin-dash__th-line">Submitted</span>
                    <span className="admin-dash__th-sub">Date &amp; time</span>
                  </span>
                </th>
                <th scope="col">
                  <span className="admin-dash__th-stack">
                    <span className="admin-dash__th-line">Name</span>
                  </span>
                </th>
                <th scope="col">
                  <span className="admin-dash__th-stack">
                    <span className="admin-dash__th-line">Email</span>
                  </span>
                </th>
                <th scope="col">
                  <span className="admin-dash__th-stack">
                    <span className="admin-dash__th-line">Newsletter</span>
                    <span className="admin-dash__th-sub">Opt-in</span>
                  </span>
                </th>
                <th scope="col">
                  <span className="admin-dash__th-stack">
                    <span className="admin-dash__th-line">Preview</span>
                    <span className="admin-dash__th-sub">Message</span>
                  </span>
                </th>
                <th scope="col">
                  <span className="admin-dash__th-stack">
                    <span className="admin-dash__th-line">Status</span>
                    <span className="admin-dash__th-sub">Pipeline</span>
                  </span>
                </th>
                <th scope="col" className="admin-dash__th-actions">
                  <span className="admin-dash__sr-only">Open detail</span>
                </th>
              </tr>
            </thead>
          <tbody>
            {apiOn && listStatus === "loading" && rows.length === 0 ? (
              renderSkeletonRows()
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="admin-dash__empty admin-dash__empty-state">
                  <span className="admin-dash__empty-state-title">{emptyMessage.title}</span>
                  <span className="admin-dash__empty-state-sub">{emptyMessage.sub}</span>
                </td>
              </tr>
            ) : (
              filtered.map((r) => {
                const raw = (r.message || r.body || "")
                  .replace(/\s+/g, " ")
                  .trim();
                const preview =
                  raw.length > 72 ? `${raw.slice(0, 72)}…` : raw || "—";
                const displayName = r.fullName || r.name || "—";
                return (
                  <tr key={r.id} className={!r.seenAt ? "admin-dash__tr-unread" : undefined}>
                    {showBulkChrome ? (
                      <td className="admin-dash__th-select">
                        <input
                          type="checkbox"
                          className="admin-dash__checkbox"
                          checked={selection.selected.has(r.id)}
                          onChange={() => selection.toggle(r.id)}
                          disabled={bulkBusy}
                          aria-label={`Select message ${displayName || r.email || r.id}`}
                        />
                      </td>
                    ) : (
                      <td className="admin-dash__th-select" aria-hidden />
                    )}
                    <td className="admin-dash__td-dot">
                      {!r.seenAt ? (
                        <span className="admin-dash__unread-dot" title="Unread" />
                      ) : null}
                    </td>
                    <td className="admin-dash__td-muted">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                    <td>{highlightSearchText(displayName, q)}</td>
                    <td>{highlightSearchText(r.email || "—", q)}</td>
                    <td>
                      {r.newsletter ? (
                        <span className="admin-dash__badge-newsletter">Yes</span>
                      ) : (
                        <span className="admin-dash__badge-muted">No</span>
                      )}
                    </td>
                    <td className="admin-dash__td-preview">
                      {highlightSearchText(preview, q)}
                    </td>
                    <td>
                      <div className="admin-dash__td-badges">
                        <span
                          className={adminStatusBadgeClass(r.status)}
                          title={adminStatusHint(r.status)}
                        >
                          {adminStatusLabel(r.status)}
                        </span>
                        {(r.replies || []).length > 0 ? (
                          <span className="admin-dash__badge-replies">
                            {(r.replies || []).length} repl
                            {(r.replies || []).length === 1 ? "y" : "ies"}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="admin-dash__td-actions">
                      <Link className="admin-dash__table-btn" to={`/admin/dashboard/contact/${r.id}`}>
                        Open
                      </Link>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
